import React from 'react';
import {
  Box,
  Typography,
  Grid,
  CircularProgress,
  Paper,
  Stack,
  Chip,
} from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { container, descContainer, rankingContainer } from './styles';
import { useAnalysisData } from 'src/api/hooks/use-analysis-data';
import { brand } from 'src/themes/palette';
import { ProfileStrengthCard, StatCard } from './card-view';
import { RatingComparison } from './rating';
import { useSearchParams } from 'react-router-dom';

const AnalysisScreen: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const { loading, data } = useAnalysisData({ token });

  if (loading) {
    return (
      <Box
        sx={container}
        alignContent="center"
        alignItems="center"
        bgcolor={brand.background}
        justifyContent="center">
        <CircularProgress color="secondary" />
      </Box>
    );
  }

  return (
    <Box sx={container}>
      <Box
        mt={2}
        sx={{
          borderRadius: 3,
          p: { xs: 3, md: 4 },
          background: `linear-gradient(135deg, ${brand.primary} 0%, ${brand.secondary} 50%, ${brand.background} 100%)`,
          color: '#fff',
          boxShadow: 6,
        }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
          justifyContent="space-between">
          <Box sx={descContainer} alignItems="flex-start" textAlign="left">
            <Typography variant="h4" gutterBottom>
              Google Maps Profile Audit
            </Typography>
            <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
              Comprehensive analysis of your business presence
            </Typography>
            <Stack direction="row" spacing={1} mt={2} flexWrap="wrap">
              {data?.name && <Chip label={data.name} color="default" />}
              {data?.date && (
                <Chip
                  label={`As of ${new Date(data.date).toLocaleDateString()}`}
                  color="default"
                />
              )}
              {typeof data?.radius === 'number' && (
                <Chip
                  label={`Radius ${(data.radius / 1000).toFixed(1)} km`}
                  color="default"
                />
              )}
            </Stack>
          </Box>
          <Stack direction="row" spacing={2}>
            <Paper
              elevation={0}
              sx={{
                px: 2.5,
                py: 1.5,
                borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.16)',
                color: 'inherit',
              }}>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                Avg Rating
              </Typography>
              <Typography variant="h5" fontWeight={700}>
                {data?.rating?.toFixed?.(1) ?? '—'}
              </Typography>
            </Paper>
            <Paper
              elevation={0}
              sx={{
                px: 2.5,
                py: 1.5,
                borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.16)',
                color: 'inherit',
              }}>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                Strength
              </Typography>
              <Typography variant="h5" fontWeight={700}>
                {data?.strength ?? '—'}%
              </Typography>
            </Paper>
          </Stack>
        </Stack>
      </Box>

      <Box mt={6} display="flex" justifyContent="center">
        <ProfileStrengthCard
          name={data?.name}
          date={data?.date}
          radius={(data?.radius ?? 0) / 1000}
          strength={data?.strength ?? 0}
        />
      </Box>

      <Box sx={rankingContainer}>
        <Grid
          sx={{
            width: '100%',
            maxWidth: 1100,
            mt: 2,
          }}
          container
          spacing={3}
          mt={4}>
          {data?.keywordsRankings?.map((ranking, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2.5,
                  height: '100%',
                  borderRadius: 3,
                  borderColor: 'divider',
                }}>
                <StatCard
                  title={ranking.keyword}
                  icon={
                    <EmojiEventsIcon
                      color={ranking.strength >= 20 ? 'error' : 'secondary'}
                    />
                  }
                  value={ranking.strength ?? 0}
                  progress={
                    ranking.strength ? ((20 - ranking.strength) / 19) * 100 : 0
                  }
                  label={
                    ranking.strength >= 20
                      ? 'Ranked lower than #20'
                      : 'Local Pack Position'
                  }
                />
                <Box mt={2}>
                  <RatingComparison
                    userRating={data.rating ?? 0}
                    competitorRating={ranking.rating ?? 0}
                  />
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
};

export { AnalysisScreen };
